import { FC, useEffect, useState } from 'react';
import Joyride, { CallBackProps, Step, Styles } from 'react-joyride';
import { createGlobalStyle } from 'styled-components';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { openRightSidebar, closeRightSidebar, setShowTour } from 'store/global/globalSlice';
import { onUpdateDoc } from 'utils/config/firebase';
import { USER_SETTINGS } from 'utils/config/constants/controllerNames';
import { getGenerateVersionsTour, getMainTour } from './constants';
import { UserSettingsType } from 'store/auth/types';
import { useParams } from 'react-router-dom';
import { CallBackPropsCustom } from './types';

const GlobalStyles = createGlobalStyle`
    .tooltip-title {
        font-size: 18px;
        font-weight: 500;
        display:flex;
        align-items: center;
        &-new{
            margin-right: 8px;
            font-family: Roboto;
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: 16px; /
            display: flex;
            padding: 6px 8px;
            justify-content: center;
            align-items: center;
            border-radius: 6px;
            background: #4EE3FE;
            height: fit-content;
        }
    }
    .__floater {
        filter: unset !important;
        // z-index:1000000 !important;
    }
`;

// const ComposingPadArrow = `
//     #react-joyride-step-3 .__floater__arrow {
//         animation: arrow-animation 225ms linear;
//         animation-delay: 1s;
//         animation-fill-mode: forwards;
//     }

//     @keyframes arrow-animation {
//         from {
//             transform: translateX(0);
//         }
//         to {
//             transform: translateX(-550px);
//         }
//     }
// `;

const GlobalStylesArrow = createGlobalStyle`
    #react-joyride-step-0 .__floater__arrow {
        left: -120px !important;
    }
`;

const GlobalStylesForCustom = createGlobalStyle`
    .tooltip-title {
        font-size: 18px;
        font-weight: 500;
    }
`;

const ProductTourTooltips: FC<{
    userSettings: UserSettingsType;
}> = ({ userSettings }) => {
    const dispatch = useAppDispatch();
    const { document_id } = useParams();

    const { showTour, isRightSideBarOpened } = useAppSelector((state) => state.global);
    const [run, setRun] = useState<boolean>(false);
    const [joyrideSteps, setJoyrideSteps] = useState<Step[]>([]);
    const [isFull, setIsFull] = useState<boolean>(false);

    useEffect(() => {
        if (run && showTour) {
            setRun((prev) => !prev);
            setTimeout(() => setRun((prev) => !prev));
        }
    }, [isRightSideBarOpened, run, showTour]);

    useEffect(() => {
        if (userSettings && userSettings.show_full_product_tour && !document_id) {
            setJoyrideSteps(getMainTour());
            setIsFull(true);
            setRun(true);
        }
    }, [document_id, userSettings]);

    useEffect(() => {
        if (
            document_id &&
            showTour &&
            !userSettings.show_full_product_tour &&
            !userSettings.dont_show_general_generate
        ) {
            setRun(true);
            setIsFull(false);
            setJoyrideSteps(getGenerateVersionsTour());
        } else if (
            !document_id &&
            showTour &&
            !userSettings.show_full_product_tour &&
            !userSettings.dont_show_general_generate
        ) {
            setRun(false);
            dispatch(setShowTour(false));
        }
    }, [
        userSettings.show_full_product_tour,
        userSettings.dont_show_general_generate,
        showTour,
        document_id,
        dispatch,
    ]);

    const updateUserSettings = (updatedSettings: Partial<UserSettingsType>) => {
        setRun(false);
        onUpdateDoc(USER_SETTINGS, userSettings.document_id, updatedSettings);
    };

    const editStepStyles: Partial<Styles> = {
        tooltip: {
            transform: 'translateX(-550px)',
            transition: 'all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
        },
    };

    const joyrideStyles: Partial<Styles> = {
        tooltip: {
            display: 'flex',
            width: 312,
            minWidth: 280,
            maxWidth: 560,
            flexDirection: 'column',
            alignItems: 'flex-end',
            borderRadius: 28,
            background: '#ECF3FC',
            boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
            padding: 24,
            filter: 'unset',
        },
        options: {
            arrowColor: '#95BBE9',
        },
        buttonNext: {
            backgroundColor: '#ECF3FC',
            color: '#2175D9',
        },
        tooltipTitle: {
            textAlign: 'left',
        },
        tooltipContent: {
            fontSize: 12,
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '16px',
            textAlign: 'left',
            paddingLeft: 0,
        },
    };
    const handleJoyrideCallback = (data: CallBackProps) => {
        const customData = { ...data } as CallBackPropsCustom;
        const { index, action, step, status } = customData;
        if (step.target === '.right-sidebar-box button' && action === 'update') {
            setTimeout(() => {
                dispatch(openRightSidebar());

                setJoyrideSteps((prevSteps) =>
                    prevSteps.map((step, currentIndex) =>
                        currentIndex === index ? { ...step, styles: editStepStyles } : step,
                    ),
                );
            }, 500);
        }
        if (step.target === '.left-sidebar') {
            dispatch(closeRightSidebar());
        }
        if (status === 'finished') {
            if (step.id === 'full-tour') {
                updateUserSettings({
                    show_full_product_tour: false,
                });
            }
            if (step.id === 'generate-btn-tour') {
                updateUserSettings({
                    dont_show_general_generate: true,
                });
            }
        }
    };

    return (
        <>
            <GlobalStyles />
            {isFull ? <GlobalStylesArrow /> : <GlobalStylesForCustom />}
            <Joyride
                steps={joyrideSteps}
                run={run}
                continuous
                scrollToFirstStep
                disableCloseOnEsc
                disableOverlayClose
                disableOverlay
                hideBackButton
                hideCloseButton
                styles={joyrideStyles}
                callback={handleJoyrideCallback}
                disableScrollParentFix={true}
            />
        </>
    );
};

export default ProductTourTooltips;
